import { apiClient } from './api-client';

// Audio Buildup Drills admin client. See docs/BUILDSPEC_AUDIO_BUILDUP_DRILLS.md
// and api/audio-course/index.js. Fon integration mirrors glossika-dialogs.

export interface ScriptMeta {
  file: string;
  lang: string;
  level: string;
  pattern: number;
  day: number;
  topic: string | null;
  edited: boolean;
  version: string;
  segments: number;
  speech: number;
  unique: number;
  uniqueChars: number;
  roles: Record<string, number>;
}

export interface ScriptLine { id: string; role: string; lang: string; text: string; }
export interface ScriptDetail {
  file: string; lang: string; version: string; edited: boolean;
  topic: string | null; lines: ScriptLine[]; transcript: string;
}

export interface FonVoice { id: string; name: string; gender: string; languageCode: string; region: string; }
export interface VoiceRole { role: string; label?: string; codes: string[]; voices: FonVoice[]; error?: string; }

export type VoiceConfig = Record<string, string>;

export interface JobStats {
  filesBuilt: number;
  totalSpeechSegments: number;
  uniqueSpeechSegments: number;
  ttsCalls: number;
  localCacheHits: number;
  fonCacheHits: number;
  cacheHitPct: number;
  charactersToFon: number;
  audioSeconds: number;
  audioMinutes: number;
  billedAudioMinutes: number;
  fonWallSeconds: number;
  buildWallSeconds: number;
  creditsUsed: number;
  usdPerAudioMin: number;
  estimatedCostUsd: number | null;
}

export interface FileResult {
  file: string; pattern: number; day: number; mp3Url: string; txtUrl: string;
  durationMs: number; bytes: number; segments: number; speech: number;
  unique: number; sourceVersion: string; cacheHitPct: number; buildMs: number;
}

export interface Job {
  id: string;
  status: 'queued' | 'running' | 'done' | 'error';
  lang: string; level: string; files: string[];
  progress: { done: number; total: number };
  results?: FileResult[];
  stats?: JobStats;
  error?: string;
}

export interface Output {
  file: string; mp3Url: string; txtUrl: string; durationMs: number; bytes: number;
  sourceVersion: string; voiceHash: string; pattern: number; day: number; updatedAt: string;
}

const BASE = '/api/admin/audio-course';

export const listScripts = (lang: string, level: string) =>
  apiClient<{ scripts: ScriptMeta[]; count: number }>(`${BASE}/scripts?lang=${lang}&level=${level}`);

export const getScript = (lang: string, file: string) =>
  apiClient<ScriptDetail>(`${BASE}/script/${encodeURIComponent(file)}?lang=${lang}`);

export const saveScript = (lang: string, file: string, lines: Record<string, string>) =>
  apiClient<{ file: string; version: string; edited: boolean; changed: number; revertedToSeed?: boolean }>(
    `${BASE}/script`, { method: 'POST', body: JSON.stringify({ lang, file, lines }) });

export const resetScript = (lang: string, file: string) =>
  apiClient<{ file: string; version: string; edited: boolean }>(
    `${BASE}/script/reset`, { method: 'POST', body: JSON.stringify({ lang, file }) });

export const getVoices = (lang: string) =>
  apiClient<{ roles: VoiceRole[]; note: string }>(`${BASE}/voices?lang=${lang}`);

export interface CourseMeta { courseName: string; artist: string; genre: string }

export const getConfig = (lang: string) =>
  apiClient<{ lang: string; config: { voiceConfig: VoiceConfig; voiceLang: VoiceConfig; usdPerAudioMin: number; meta: CourseMeta; voiceHash: string } }>(
    `${BASE}/config?lang=${lang}`);

export const saveConfig = (lang: string, voiceConfig: VoiceConfig, usdPerAudioMin: number, voiceLang: VoiceConfig) =>
  apiClient<{ voiceHash: string }>(`${BASE}/config`, { method: 'POST', body: JSON.stringify({ lang, voiceConfig, voiceLang, usdPerAudioMin }) });

// Save just the MP3-tag metadata for a course (backend preserves the voice config).
export const saveCourseMeta = (lang: string, meta: CourseMeta) =>
  apiClient<{ saved: boolean; meta: CourseMeta }>(`${BASE}/config`, { method: 'POST', body: JSON.stringify({ lang, meta }) });

// Re-tag every already-built MP3 with the current metadata (no audio rebuild, no Fon cost).
export const retagAll = () =>
  apiClient<{ retag: string }>(`${BASE}/build`, { method: 'POST', body: JSON.stringify({ retag: true }) });

export const build = (lang: string, level: string, files: string[], voiceConfig?: VoiceConfig, usdPerAudioMin?: number, voiceLang?: VoiceConfig) =>
  apiClient<{ jobId: string; status: string; files: string[] }>(
    `${BASE}/build`, { method: 'POST', body: JSON.stringify({ lang, level, files, voiceConfig, usdPerAudioMin, voiceLang }) });

export const getJob = (id: string) => apiClient<{ job: Job }>(`${BASE}/job/${encodeURIComponent(id)}`);

export const listOutputs = (lang: string, level: string) =>
  apiClient<{ lang: string; level: string; outputs: Output[] }>(`${BASE}/outputs?lang=${lang}&level=${level}`);

// Landing-page samples. Audio: cut a clip from a built course MP3 (capped at 60s).
// Page: render the matching companion book page (same pattern) to a public PNG.
export const makeSample = (lang: string, level: string, file: string, duration = 60, start = 0) =>
  apiClient<{ sample_url: string; key: string; file: string }>(`${BASE}/sample`, {
    method: 'POST', body: JSON.stringify({ lang, level, file, start: Math.max(0, start), duration: Math.min(60, Math.max(5, duration)) }),
  });

export const makeSamplePage = (line: string, language: string, level: string, chapter: number) =>
  apiClient<{ ok: boolean; sample_pages: Record<string, string> }>(`/api/admin/books/sample-images`, {
    // no perPage → the server fills the whole book page (entries clip at the page edge like a real page).
    method: 'POST', body: JSON.stringify({ line, language, level, chapters: [chapter] }),
  });

// Render the book's front COVER page (title + framed image + branding) to a public PNG.
// coverImageUrl is an optional illustration fallback when the book has no saved cover.
export const makeCoverPage = (line: string, language: string, level: string, coverImageUrl?: string, title?: string) =>
  apiClient<{ ok: boolean; cover_url?: string; error?: string }>(`/api/admin/books/sample-images`, {
    method: 'POST', body: JSON.stringify({ line, language, level, cover: true, coverImageUrl, title }),
  });

// ---- Store copy (landing-page content per product) -------------------------
export interface StoreVars {
  anchor_high: string; anchor_low: string; your_price: string;
  bundle_price: string; countdown: string; donation_amount: string;
}
export interface StoreCopy {
  title: string;
  author: string;
  product_id: string;
  cover_image_url: string;            // product cover shown on the store card
  vars: StoreVars;
  blocks: Record<string, string>;   // b1..b17
  bonuses: string;                    // one bonus name per line
  bonus_covers: string;               // one cover URL per line, matching the bonuses order
  upsell: string;
  fund_line: string;
  sample_audio: string;              // one URL per line
  sample_pages: string;              // one URL per line
  success_instructions: string;      // post-purchase: how to use the product
  success_upsell: string;            // post-purchase: Pro/Plus subscription upsell
}

export const getStore = (lang: string, level: string) =>
  apiClient<{ lang: string; level: string; store: StoreCopy | null; updatedAt: string | null }>(
    `${BASE}/store?lang=${lang}&level=${level}`);

export const saveStore = (lang: string, level: string, store: StoreCopy) =>
  apiClient<{ saved: boolean; lang: string; level: string }>(
    `${BASE}/store`, { method: 'POST', body: JSON.stringify({ lang, level, store }) });

export interface PublishResult { ok: boolean; listing_id?: string; store_url?: string; version?: number; status?: number; detail?: string; }

// Publish/Update is one idempotent call (store upserts by product_id); the backend adds
// the X-Content-Key secret and bumps the version.
export const publishStore = (lang: string, level: string, payload: unknown) =>
  apiClient<PublishResult>(`${BASE}/publish`, { method: 'POST', body: JSON.stringify({ lang, level, payload }) });

export const unpublishStore = (lang: string, level: string, product_id: string) =>
  apiClient<PublishResult>(`${BASE}/unpublish`, { method: 'POST', body: JSON.stringify({ lang, level, product_id }) });

// Upload a chosen/cropped cover image (base64) and get back a permanent CDN URL. Reuses the
// books image-upload endpoint with a neutral 'audio_cover' slot so it never touches a book's
// own cover record.
export const uploadStoreCover = (regionCode: string, level: string, imageBase64: string) =>
  apiClient<{ url: string; s3_key: string }>(`/api/admin/books/upload-image`, {
    method: 'POST',
    body: JSON.stringify({ language_code: regionCode, level, image_base64: imageBase64, content_type: 'image/jpeg', slot: 'audio_cover' }),
  });

// ---- LDP (landing-page) generation: shared TEMPLATE + per-product FIELDS ----
export interface LdpMeta { classes: { A: string[]; B: string[]; C: string[] }; optional: string[]; injected: string[]; guidance: Record<string, string>; }
export const getLdpMeta = () => apiClient<LdpMeta>(`${BASE}/ldp/meta`);

export interface LdpTemplate { level: string; template_id: string; version: number; status: string; body: string; slots: string[]; scaffolded?: boolean; }
export const getLdpTemplate = (level: string) => apiClient<LdpTemplate>(`${BASE}/ldp/template?level=${level}`);

export interface LdpFields {
  lang: string; level: string; template_id: string; template_version: number;
  fields: Record<string, string>; reviewed: Record<string, boolean>; prose?: Record<string, string>;
  status: string; rev: number; updatedAt: string | null; body?: string; slots: string[]; scaffolded?: boolean; seeded?: boolean;
}
export const getLdpFields = (lang: string, level: string) => apiClient<LdpFields>(`${BASE}/ldp/fields?lang=${lang}&level=${level}`);
export const saveLdpFields = (lang: string, level: string, fields: Record<string, string>, reviewed?: Record<string, boolean>, prose?: Record<string, string>) =>
  apiClient<{ saved: boolean; rev: number; savedAt: string }>(`${BASE}/ldp/fields`, { method: 'POST', body: JSON.stringify({ lang, level, fields, reviewed, prose }) });

export interface LdpHistoryEntry { savedAt: string; rev: number; fieldCount: number; }
export const getLdpHistory = (lang: string, level: string) => apiClient<{ history: LdpHistoryEntry[] }>(`${BASE}/ldp/fields/history?lang=${lang}&level=${level}`);
export const restoreLdpFields = (lang: string, level: string, savedAt: string, rev: number) =>
  apiClient<{ saved: boolean; rev: number }>(`${BASE}/ldp/fields/restore`, { method: 'POST', body: JSON.stringify({ lang, level, savedAt, rev }) });

export const getLdpReferences = () => apiClient<{ references: { CMN_TW: Record<string, string> | null; RUS: Record<string, string> | null } }>(`${BASE}/ldp/references`);

export interface LdpRenderResult { markdown: string; unfilled: string[]; blocking: string[]; warnings: string[]; completeness: { filled: number; required: number; missing: string[] }; }
export const renderLdp = (lang: string, level: string) => apiClient<LdpRenderResult>(`${BASE}/ldp/render`, { method: 'POST', body: JSON.stringify({ lang, level }) });

export interface LdpPublishResult { ok: boolean; blocked?: boolean; blocking?: string[]; warnings?: string[]; listing_id?: string; store_url?: string; version?: number; status?: number; detail?: string; }
export const publishLdp = (lang: string, level: string) => apiClient<LdpPublishResult>(`${BASE}/ldp/publish`, { method: 'POST', body: JSON.stringify({ lang, level }) });
